import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@card-software/library';
import {
  InventorySession,
  InventorySessionScan,
  InventorySessionSearchCommand,
  InventorySessionSearchResult,
  InventorySessionScanSearchResult,
  CreateInventorySessionRequest,
  CreateChildInventorySessionRequest,
  InventorySessionStatus,
} from '../../models/inventory-session/inventory-session';

@Injectable({
  providedIn: 'root',
})
export class InventorySessionProxyService {
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/inventory-session';

  constructor(private http: HttpClient) {}

  public create(request: CreateInventorySessionRequest): Observable<ApiResponse<InventorySession>> {
    return this.http.post<ApiResponse<InventorySession>>(this.baseUrl, request);
  }

  public createChild(parentId: string, request: CreateChildInventorySessionRequest): Observable<ApiResponse<InventorySession>> {
    return this.http.post<ApiResponse<InventorySession>>(`${this.baseUrl}/${parentId}/children`, request);
  }

  public getById(id: string): Observable<ApiResponse<InventorySession>> {
    return this.http.get<ApiResponse<InventorySession>>(`${this.baseUrl}/${id}`);
  }

  public getChildren(parentId: string): Observable<ApiResponse<InventorySession[]>> {
    return this.http.get<ApiResponse<InventorySession[]>>(`${this.baseUrl}/${parentId}/children`);
  }

  public search(
    command: InventorySessionSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<InventorySessionSearchResult>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.http.post<ApiResponse<InventorySessionSearchResult>>(url, command);
  }

  public updateStatus(id: string, status: InventorySessionStatus): Observable<ApiResponse<InventorySession>> {
    return this.http.patch<ApiResponse<InventorySession>>(`${this.baseUrl}/${id}/status`, { status });
  }

  public scan(sessionId: string, lotItemNumber: string): Observable<ApiResponse<InventorySessionScan>> {
    return this.http.post<ApiResponse<InventorySessionScan>>(`${this.baseUrl}/${sessionId}/scan`, { lotItemNumber });
  }

  public getScans(sessionId: string, pageNumber = 1, pageSize = 50): Observable<ApiResponse<InventorySessionScanSearchResult>> {
    return this.http.get<ApiResponse<InventorySessionScanSearchResult>>(
      `${this.baseUrl}/${sessionId}/scans?pageNumber=${pageNumber}&pageSize=${pageSize}`,
    );
  }

  public deleteScan(parentId: string, childId: string, lotItemNumber: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseUrl}/${parentId}/children/${childId}/scans/${encodeURIComponent(lotItemNumber)}`,
    );
  }

  public export(id: string, detailed = false): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/export?detailed=${detailed}`, { responseType: 'blob' });
  }
}
